/**
 * Phase 13 / Task 13.2b (migration 0235) — tank readings entered in the portal.
 *
 * GET / POST /v1/operator-portal/tanks/:tankId/readings and PATCH …/:readingId/void. :tankId is
 * the hauler tank the Tanks page lists; the reading is stored on the operator's own tank it links
 * to, so every hauler of that tank sees it. Types mirror
 * packages/api/src/routes/operator-portal/tank-readings.ts (copied by hand). A write refreshes the
 * Tanks page, whose level and fill rate use the readings.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { PORTAL_TANKS_KEY } from './useOperatorPortal';

export interface PortalTankReadingRow {
  readonly id: string;
  /**
   * Tank registry PR 1 (0245): null when the reading was given in inches and the tank has no bbl
   * per inch yet ("level unknown" until it gets a size).
   */
  readonly levelBbl: number | null;
  /** The inches given (a POST answer); list rows carry null. */
  readonly levelInches: number | null;
  readonly readAt: string;
  readonly source: string;
  /** True for the company's own readings (voidable); false for the hauler's dispatchers'. */
  readonly own: boolean;
  readonly voidedAt: string | null;
}

export interface PortalTankReadingsResponse {
  /** False until the tank is set up (linked to one of the company's tanks): no reading yet. */
  readonly setUp: boolean;
  readonly readings: readonly PortalTankReadingRow[];
}

/** Exactly one of levelBbl or levelInches (tank registry PR 1). */
export type AddPortalTankReadingBody = (
  | { levelBbl: number; levelInches?: never }
  | { levelInches: number; levelBbl?: never }
) & {
  readAt?: string;
  /** One per opening of the form, so a retry stores one reading. */
  clientKey: string;
};

export const portalTankReadingsKey = (tankId: string): readonly unknown[] => [
  'operator-portal',
  'tanks',
  tankId,
  'readings',
];

export function usePortalTankReadings(
  tankId: string,
  enabled: boolean,
): ReturnType<typeof useApiQuery<PortalTankReadingsResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PortalTankReadingsResponse>(
    opts,
    `/v1/operator-portal/tanks/${tankId}/readings`,
    { queryKey: portalTankReadingsKey(tankId), enabled },
  );
}

export function useAddPortalTankReading(
  tankId: string,
): ReturnType<typeof useApiMutation<AddPortalTankReadingBody, { reading: PortalTankReadingRow }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<AddPortalTankReadingBody, { reading: PortalTankReadingRow }>(
    opts,
    `/v1/operator-portal/tanks/${tankId}/readings`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: portalTankReadingsKey(tankId) });
        void qc.invalidateQueries({ queryKey: PORTAL_TANKS_KEY });
      },
    },
  );
}

export function useVoidPortalTankReading(
  tankId: string,
  readingId: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { reading: PortalTankReadingRow }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { reading: PortalTankReadingRow }>(
    opts,
    `/v1/operator-portal/tanks/${tankId}/readings/${readingId}/void`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: portalTankReadingsKey(tankId) });
        void qc.invalidateQueries({ queryKey: PORTAL_TANKS_KEY });
      },
    },
  );
}
